import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AUTH_ROUTES,
  savedAddressesResponseSchema,
  type AddressInput,
  type SavedAddress,
} from "@nh/shared";
import { authFetch } from "@/shared/api/session";

const NO_PREFIX = { baseUrl: "" } as const;

export const addressKeys = { mine: ["me", "addresses"] as const };

/** Las direcciones guardadas de la sesión. */
export function useMyAddresses() {
  return useQuery({
    queryKey: addressKeys.mine,
    queryFn: async () => {
      const data = await authFetch(AUTH_ROUTES.addresses, savedAddressesResponseSchema, NO_PREFIX);
      return data.addresses;
    },
  });
}

/**
 * Todas las escrituras devuelven LA LISTA COMPLETA y se guarda tal cual:
 * el servidor es quien decide cuál queda de predeterminada tras un
 * borrado, y adivinarlo aquí sería inventarse la mitad de la verdad.
 */
function useAddressMutation<TVars>(
  run: (vars: TVars) => Promise<SavedAddress[]>,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: run,
    onSuccess: (addresses) => queryClient.setQueryData(addressKeys.mine, addresses),
  });
}

const write = (path: string, method: string, body?: unknown) =>
  authFetch(path, savedAddressesResponseSchema, { ...NO_PREFIX, method, ...(body ? { body } : {}) })
    .then((data) => data.addresses);

export function useAddAddress() {
  return useAddressMutation((input: AddressInput) =>
    write(AUTH_ROUTES.addresses, "POST", input),
  );
}

export function useUpdateAddress() {
  return useAddressMutation(({ id, input }: { id: string; input: AddressInput }) =>
    write(AUTH_ROUTES.address(id), "PUT", input),
  );
}

export function useRemoveAddress() {
  return useAddressMutation((id: string) => write(AUTH_ROUTES.address(id), "DELETE"));
}

export function useMakeDefaultAddress() {
  return useAddressMutation((id: string) => write(AUTH_ROUTES.defaultAddress(id), "PATCH", {}));
}
